/* NutriChef sections — progressive enhancement for the nc-* sections.
   Content is fully visible without this file (html.nc-js is only kept
   once it runs). No dependencies. */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  window.NCSections = true;
  root.classList.add('nc-js');

  /* ---------- Reveal on scroll ---------- */
  /* Elements hidden with clip-path never report as intersecting, so those
     are watched through their parent instead. */
  const revealTargets = new WeakMap();
  const revealObserver =
    'IntersectionObserver' in window && !reduceMotion
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              (revealTargets.get(entry.target) || [entry.target]).forEach((el) => el.classList.add('is-in'));
              revealTargets.delete(entry.target);
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        )
      : null;

  const reveal = (el) => {
    if (!revealObserver || designMode) {
      el.classList.add('is-in');
      return;
    }
    const watch = el.dataset.ncReveal === 'clip' && el.parentElement ? el.parentElement : el;
    revealTargets.set(watch, [...(revealTargets.get(watch) || []), el]);
    revealObserver.observe(watch);
  };

  /* ---------- Word-mask headlines ----------
     Wraps each word (keeping inline markup such as <em>) so it can rise
     out of its own mask. */
  const splitWords = (el) => {
    let index = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/);
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part));
              return;
            }
            const outer = document.createElement('span');
            outer.className = 'nc-word';
            const inner = document.createElement('span');
            inner.style.setProperty('--l', index++);
            inner.textContent = part;
            outer.appendChild(inner);
            frag.appendChild(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
  };

  /* ---------- Counters ---------- */
  const animateCount = (el) => {
    const raw = el.textContent.trim();
    const match = raw.match(/^([^\d]*)([\d,.]+)(.*)$/);
    if (!match || reduceMotion || designMode) return;
    const [, prefix, num, suffix] = match;
    const target = parseFloat(num.replace(/,/g, ''));
    const decimals = (num.split('.')[1] || '').length;
    const useComma = num.includes(',');
    const format = (v) => {
      const fixed = v.toFixed(decimals);
      return useComma ? Number(fixed).toLocaleString('en-US', { minimumFractionDigits: decimals }) : fixed;
    };
    const duration = 1600;
    let start;
    el.textContent = prefix + format(0) + suffix;
    const tick = (t) => {
      start = start || t;
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      el.textContent = prefix + format(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = raw;
    };
    requestAnimationFrame(tick);
  };

  const countObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              animateCount(entry.target);
              countObserver.unobserve(entry.target);
            });
          },
          { threshold: 0.6 }
        )
      : null;

  /* ---------- Parallax ---------- */
  const parallaxItems = new Set();
  let ticking = false;
  const updateParallax = () => {
    ticking = false;
    const vh = window.innerHeight;
    parallaxItems.forEach((el) => {
      if (!el.isConnected) {
        parallaxItems.delete(el);
        return;
      }
      const rect = el.parentElement.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > vh + 200) return;
      const speed = parseFloat(el.dataset.ncParallax) || 0.08;
      const offset = (rect.top + rect.height / 2 - vh / 2) * -speed;
      el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    });
  };
  const requestParallax = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateParallax);
  };
  if (!reduceMotion) {
    window.addEventListener('scroll', requestParallax, { passive: true });
    window.addEventListener('resize', requestParallax);
  }

  /* ---------- Plan tabs ---------- */
  const activateTab = (tablist, tab, focus) => {
    const tabs = [...tablist.querySelectorAll('[role="tab"]')];
    tabs.forEach((t) => {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected);
      t.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (!panel) return;
      if (selected) {
        const wasHidden = panel.hidden;
        panel.hidden = false;
        if (wasHidden && !reduceMotion) {
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
        }
        panel.querySelectorAll('grid-slider').forEach((slider) => {
          slider.dispatchEvent(new CustomEvent('theme:grid-slider:init', { bubbles: true }));
        });
      } else {
        panel.hidden = true;
        panel.classList.remove('is-entering');
      }
    });
    if (focus) tab.focus();
    const strip = tablist;
    if (strip.scrollWidth > strip.clientWidth) {
      const left = tab.offsetLeft - strip.clientWidth / 2 + tab.clientWidth / 2;
      strip.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  };

  const initTabs = (scope) => {
    scope.querySelectorAll('[data-nc-tabs]').forEach((tablist) => {
      if (tablist.dataset.ncReady) return;
      tablist.dataset.ncReady = '1';
      const tabs = [...tablist.querySelectorAll('[role="tab"]')];
      tablist.addEventListener('click', (e) => {
        const tab = e.target.closest('[role="tab"]');
        if (tab) activateTab(tablist, tab, false);
      });
      tablist.addEventListener('keydown', (e) => {
        const i = tabs.indexOf(document.activeElement);
        if (i < 0) return;
        let next;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
        if (e.key === 'Home') next = tabs[0];
        if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          activateTab(tablist, next, true);
        }
      });
    });
  };

  /* ---------- FAQ: smooth <details> ---------- */
  const closeFaqDetails = (details) =>
    new Promise((resolve) => {
      const answer = details.querySelector('.nc-faq__answer');
      if (!details.open || !answer) {
        details.open = false;
        resolve();
        return;
      }
      if (reduceMotion || !answer.animate) {
        details.open = false;
        resolve();
        return;
      }
      details.dataset.animating = '1';
      const from = answer.scrollHeight;
      answer.style.height = `${from}px`;
      const anim = answer.animate(
        { height: [`${from}px`, '0px'], opacity: [1, 0] },
        { duration: 360, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' }
      );
      anim.onfinish = () => {
        details.open = false;
        answer.style.height = '';
        answer.style.opacity = '';
        anim.cancel();
        delete details.dataset.animating;
        resolve();
      };
    });

  const openFaqDetails = (details) =>
    new Promise((resolve) => {
      const answer = details.querySelector('.nc-faq__answer');
      if (!answer) {
        details.open = true;
        resolve();
        return;
      }
      if (reduceMotion || !answer.animate) {
        details.open = true;
        resolve();
        return;
      }
      details.dataset.animating = '1';
      details.open = true;
      const to = answer.scrollHeight;
      answer.style.height = '0px';
      answer.style.opacity = '0';
      const anim = answer.animate(
        { height: ['0px', `${to}px`], opacity: [0, 1] },
        { duration: 420, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' }
      );
      anim.onfinish = () => {
        answer.style.height = '';
        answer.style.opacity = '';
        anim.cancel();
        delete details.dataset.animating;
        resolve();
      };
    });

  const initFaq = (scope) => {
    scope.querySelectorAll('[data-nc-faq]').forEach((list) => {
      if (list.dataset.ncReady) return;
      list.dataset.ncReady = '1';
      const single = list.dataset.ncFaq === 'single';
      list.querySelectorAll('details').forEach((details) => {
        const summary = details.querySelector('summary');
        const answer = details.querySelector('.nc-faq__answer');
        if (!summary || !answer) return;
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          if (details.dataset.animating === '1') return;
          if (details.open) {
            closeFaqDetails(details);
            return;
          }
          const openOthers = single
            ? [...list.querySelectorAll('details[open]')].filter((other) => other !== details)
            : [];
          Promise.all(openOthers.map(closeFaqDetails)).then(() => openFaqDetails(details));
        });
      });
    });
  };

  /* ---------- Init ---------- */
  const init = (scope = document) => {
    scope.querySelectorAll('[data-nc-split]:not(.nc-lines)').forEach((el) => {
      splitWords(el);
      el.classList.add('nc-lines');
      reveal(el);
    });
    scope.querySelectorAll('[data-nc-reveal]').forEach(reveal);
    scope.querySelectorAll('[data-nc-count]').forEach((el) => countObserver && countObserver.observe(el));
    if (!reduceMotion) scope.querySelectorAll('[data-nc-parallax]').forEach((el) => parallaxItems.add(el));
    initTabs(scope);
    initFaq(scope);
    requestParallax();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  /* Theme editor support */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const tab = e.target.closest('[role="tab"]') || document.querySelector(`[role="tab"][data-block-id="${e.detail.blockId}"]`);
    const panel = e.target.closest('[role="tabpanel"]');
    const target = tab || (panel && document.querySelector(`[aria-controls="${panel.id}"]`));
    if (target) activateTab(target.closest('[data-nc-tabs]'), target, false);
  });
})();
